import React, { useState, useRef, useEffect } from 'react';
import { useStyles } from './AIEduDemoPage.styles';
import childDemoAudio from './childDemo/child-demo-audio.mp3';
import childDemoPartOne from './childDemo/child-demo-story-part1.jpg';
import childDemoPartTwo from './childDemo/child-demo-story-part2.jpg';
import childDemoPartThree from './childDemo/child-demo-story-part3.jpg';
import childDemoPartFour from './childDemo/child-demo-story-part4.jpg';
import childDemoTranscript from './childDemo/transcript';

import hijaeDemoAudio from './hijaeDemo/hijae-demo-audio.mp3';
import hijaeDemoPartOne from './hijaeDemo/hijae-demo-story-part1.png';
import hijaeDemoPartTwo from './hijaeDemo/hijae-demo-story-part2.png';
import hijaeDemoPartThree from './hijaeDemo/hijae-demo-story-part3.png';
import hijaeDemoPartFour from './hijaeDemo/hijae-demo-story-part4.png';
import hijaeDemoPartFive from './hijaeDemo/hijae-demo-story-part5.png';
import hijaeDemoTranscript from './hijaeDemo/transcript';

import seanDemoAudio from './seanDemo/sean-demo-audio.mp3';
import seanDemoPartOne from './seanDemo/sean-demo-story-part1.png';
import seanDemoPartTwo from './seanDemo/sean-demo-story-part2.png';
import seanDemoPartThree from './seanDemo/sean-demo-story-part3.png';
import seanDemoPartFour from './seanDemo/sean-demo-story-part4.png';
import seanDemoPartFive from './seanDemo/sean-demo-story-part5.png';
import seanDemoPartSix from './seanDemo/sean-demo-story-part6.png';
import seanDemoTranscript from './seanDemo/transcript';

export default function AudioNavigator() {
  const classes = useStyles();
  const [activeStoryIdx, setActiveStoryIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentImage, setCurrentImage] = useState(childDemoPartOne);
  const [currentIdx, setCurrentIdx] = useState(null);
  const audioRef = useRef();

  const STORIES = [
    {
      title: 'Child Demo',
      audio: childDemoAudio,
      transcript: childDemoTranscript,
      imageByWordIdx: {
        0: childDemoPartOne,
        25: childDemoPartTwo,
        45: childDemoPartThree,
        64: childDemoPartFour,
      },
      defaultImage: childDemoPartOne,
    },
    {
      title: 'Hijae Demo',
      audio: hijaeDemoAudio,
      transcript: hijaeDemoTranscript,
      imageByWordIdx: {
        0: hijaeDemoPartOne,
        20: hijaeDemoPartTwo,
        46: hijaeDemoPartThree,
        65: hijaeDemoPartFour,
        81: hijaeDemoPartFive,
      },
      defaultImage: hijaeDemoPartOne,
    },
    {
      title: 'Sean Demo',
      audio: seanDemoAudio,
      transcript: seanDemoTranscript,
      imageByWordIdx: {
        0: seanDemoPartOne,
        6: seanDemoPartTwo,
        15: seanDemoPartThree,
        21: seanDemoPartFour,
        28: seanDemoPartFive,
        43: seanDemoPartSix,
      },
      defaultImage: seanDemoPartOne,
    },
  ];

  const { audio, transcript, imageByWordIdx, defaultImage } = STORIES[activeStoryIdx];

  // Reset state when story changes
  useEffect(() => {
    setCurrentIdx(null);
    setCurrentImage(defaultImage);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [activeStoryIdx, defaultImage]);

  // Handle audio time updates
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      const t = audio.currentTime * 1000;
      const idx = transcript.findIndex(({ startMs, endMs }) => t >= startMs && t < endMs);
      if (idx !== -1) {
        setCurrentIdx(idx);
      }
      const wordPoints = Object.keys(imageByWordIdx)
        .map(n => parseInt(n))
        .sort((a, b) => a - b);
      for (let i = wordPoints.length - 1; i >= 0; i--) {
        if (idx >= wordPoints[i]) {
          setCurrentImage(imageByWordIdx[wordPoints[i]]);
          break;
        }
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    return () => audio.removeEventListener('timeupdate', onTimeUpdate);
  }, [activeStoryIdx, transcript, imageByWordIdx]);

  // Handle word click
  const handleWordClick = i => {
    const { startMs } = transcript[i];
    audioRef.current.currentTime = startMs / 1000;
    audioRef.current.play();
    setCurrentIdx(i);
  };

  // Sync play/pause state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [activeStoryIdx]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play();
    else audio.pause();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', maxWidth: '900px' }}>
      <div style={{ display: 'flex', gap: '0.5em' }}>
        {STORIES.map((s, i) => (
          <button
            key={i}
            onClick={() => {
              setActiveStoryIdx(i);
              setIsPlaying(false);
            }}
            style={{
              padding: '0.5em 1em',
              background: i === activeStoryIdx ? '#333' : '#eee',
              color: i === activeStoryIdx ? '#fff' : '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {s.title}
          </button>
        ))}
      </div>

      <div
        onClick={togglePlay}
        className={classes.playButton}
      >
        {isPlaying ? '⏸ Pause' : '▶️ Play'}
      </div>

      <audio key={activeStoryIdx} ref={audioRef} src={audio} style={{ display: 'none' }} />

      <img src={currentImage} alt="scene" className={classes.image} />

      <p className={classes.transcript}>
        {transcript.map(({ word }, i) => (
          <span
            key={i}
            onClick={() => handleWordClick(i)}
            style={{
              cursor: 'pointer',
              WebkitTextFillColor: i > currentIdx ? '#000' : 'transparent',
              WebkitTextStroke: '1px #000',
              transition: 'WebkitTextFillColor 0.2s',
              WebkitTextStrokeWidth: '1px',
            }}
          >
            {word}{' '}
          </span>
        ))}
      </p>
    </div>
  );
}
